import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  Inject,
  PLATFORM_ID
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HomePageDataService } from '../../../../services/homedata';


interface HomeTotals {
  total_jobs: number;
  total_doctors: number;
  total_companies: number;
}

interface HomeTotalsResponse {
  status: boolean;
  message: string;
  data: HomeTotals;
}

type TotalKey = keyof HomeTotals;

interface StatsCard {
  key: TotalKey;
  iconImg: string;
  tag: string;
  title: string;
  text: string;
}

@Component({
  selector: 'app-statistics',
  templateUrl: './statistics.component.html',
  styleUrl: './statistics.component.scss'
})
export class StatisticsComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('statisticsSection') statisticsSection!: ElementRef<HTMLElement>;

  totals: HomeTotals = {
    total_jobs: 0,
    total_doctors: 0,
    total_companies: 0
  };

  displayTotals: HomeTotals = {
    total_jobs: 0,
    total_doctors: 0,
    total_companies: 0
  };

statsCards: StatsCard[] = [
  {
    key: 'total_jobs',
    iconImg: '/assets/as/4.webp',
    tag: 'atw.statistics.jobsTag',
    title: 'atw.statistics.totalJobs',
    text: 'atw.statistics.totalJobsText'
  },
  {
    key: 'total_doctors',
    iconImg: '/assets/as/16.webp',
    tag: 'atw.statistics.candidatesTag',
    title: 'atw.statistics.totalDoctors',
    text: 'atw.statistics.totalDoctorsText'
  },
  {
    key: 'total_companies',
    iconImg: '/assets/as/9.webp',
    tag: 'atw.statistics.companiesTag',
    title: 'atw.statistics.totalCompanies',
    text: 'atw.statistics.totalCompaniesText'
  }
];

  isLoading = true;
  errorMessage = '';

  private observer?: IntersectionObserver;
  private isBrowser = false;
  private isSectionVisible = false;
  private hasAnimated = false;
  private animationFrames: number[] = [];

  constructor(
    private homePageDataService: HomePageDataService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.getTotals();
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }

    this.initScrollObserver();
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.animationFrames.forEach(frameId => cancelAnimationFrame(frameId));
  }

  private getTotals(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.homePageDataService.getHomePageTotals().subscribe({
      next: (res: HomeTotalsResponse) => {
        const data = res?.data;

        this.totals = {
          total_jobs: this.toNumber(data?.total_jobs),
          total_doctors: this.toNumber(data?.total_doctors),
          total_companies: this.toNumber(data?.total_companies)
        };

        this.isLoading = false;

        if (!this.isBrowser) {
          this.displayTotals = { ...this.totals };
          return;
        }

        this.tryStartCounters();
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Failed to load statistics.';
      }
    });
  }

  private initScrollObserver(): void {
    if (!this.statisticsSection?.nativeElement) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.isSectionVisible = true;
      this.tryStartCounters();
      return;
    }

    this.observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];

        if (entry.isIntersecting) {
          this.isSectionVisible = true;
          this.tryStartCounters();

          if (this.observer) {
            this.observer.disconnect();
          }
        }
      },
      {
        threshold: 0.35,
        rootMargin: '0px 0px -80px 0px'
      }
    );

    this.observer.observe(this.statisticsSection.nativeElement);
  }

  private tryStartCounters(): void {
    if (this.hasAnimated || this.isLoading || !this.isSectionVisible) {
      return;
    }

    this.hasAnimated = true;

    this.animateCounter('total_jobs', this.totals.total_jobs);
    this.animateCounter('total_doctors', this.totals.total_doctors);
    this.animateCounter('total_companies', this.totals.total_companies);
  }

  private animateCounter(key: TotalKey, endValue: number): void {
    const duration = 1500;
    const startTime = performance.now();

    const step = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      this.displayTotals[key] = Math.floor(endValue * easedProgress);

      if (progress < 1) {
        const frameId = requestAnimationFrame(step);
        this.animationFrames.push(frameId);
      } else {
        this.displayTotals[key] = endValue;
      }
    };

    const frameId = requestAnimationFrame(step);
    this.animationFrames.push(frameId);
  }

  private toNumber(value: any): number {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
  }
}